import React from 'react';
import { useTranslation } from 'react-i18next';

import { InlineAlert, Input } from '@amzn/storm-ui';
import { FormRow } from '@amzn/storm-ui-column-form-kit';

import { TAG_TYPES } from '../../../../Shared/constants/commonConstants';
import { StatusTypes } from '../../../../Shared/constants/statusTypes';
import { FilterDropdownWrapper } from '../../../../Shared/components/FilterDropdownWrapper/FilterDropdownWrapper';
import { FormFieldWrapperProps } from '../interface/FormFieldInterfaces';

export const FormFieldWrapper: React.FC<FormFieldWrapperProps> = ({
  id,
  label,
  type,
  value,
  onChange,
  placeholder,
  disabled = false,
  optional = false,
  options = [],
  persistentBottomOption,
  maxLength,
  validation,
  error = '',
}) => {
  const { t } = useTranslation();

  const renderControl = (componentId: string) => {
    if (type === TAG_TYPES.INPUT_TAG_TYPE) {
      return (
        <>
          <Input
            id={componentId}
            fullWidth
            value={value}
            onChange={(e) => {
              const newValue = e.target.value;
              if (!validation || validation(newValue)) {
                onChange?.(newValue);
              }
            }}
            placeholder={t(placeholder || '')}
            disabled={disabled}
            maxLength={maxLength}
            {...(error && { statusType: StatusTypes.ERROR })}
          />
          {error && <InlineAlert message={error} variant="error" />}
        </>
      );
    }

    // For dropdown type, ensure selected value is in options
    let dropdownOptions = [...options];

    const isPersistentBottomValue =
      persistentBottomOption &&
      value?.toString() === persistentBottomOption.value;

    if (
      value &&
      !isPersistentBottomValue &&
      !dropdownOptions.some(
        (option) => option.value.toString() === value.toString()
      )
    ) {
      const valueStr = value.toString();
      dropdownOptions.push({
        id: `selected-${valueStr}`,
        value: valueStr,
        label: valueStr,
      });
    }

    return (
      <FilterDropdownWrapper
        componentId={componentId}
        id={id}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
        error={error}
        dropdownOptions={dropdownOptions}
        persistentBottomOption={persistentBottomOption}
      />
    );
  };

  // If no label is provided, render controls directly without FormRow wrapper
  if (!label || label.trim() === '') return <>{renderControl(id)}</>;

  return (
    <FormRow
      id={id}
      label={t(label)}
      optionalTag={optional ? t('optional-tag') : undefined}
      controlComponents={[renderControl]}
    />
  );
};
